import { useCallback, useEffect, useState } from 'react';

import {
  NoshowAlertFilters,
  NoshowAlertStats,
  NoshowAlertsResponse,
  NoshowAlertDetailResponse,
  NoshowSmsMessage,
  NoshowStatsResponse,
  ResolutionType,
} from '../types/noshowAlerts';
import { axiosRubyClient as api } from '@/util/axios/axiosClient';

export const noshowAlertsAPI = {
  getAlerts: async (
    filters: NoshowAlertFilters,
  ): Promise<NoshowAlertsResponse> => {
    const params: Record<string, any> = { ...filters };
    if (Array.isArray(filters.status)) {
      params['status[]'] = filters.status;
      delete params.status;
    }
    const response = await api.get('/api/admin/noshow_alerts', { params });
    return response.data;
  },

  getAlert: async (id: number): Promise<NoshowAlertDetailResponse> => {
    const response = await api.get(`/api/admin/noshow_alerts/${id}`);
    return response.data;
  },

  getStats: async (
    dateFrom?: string,
    dateTo?: string,
  ): Promise<NoshowStatsResponse> => {
    const response = await api.get('/api/admin/noshow_alerts/stats', {
      params: { date_from: dateFrom, date_to: dateTo },
    });
    return response.data;
  },

  resolveAlert: async (
    id: number,
    notes: string,
    resolutionType: ResolutionType,
    backfillJobUrl?: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/api/admin/noshow_alerts/${id}/resolve`, {
      notes,
      resolution_type: resolutionType,
      backfill_job_url: backfillJobUrl,
    });
    return response.data;
  },

  updateAlert: async (
    id: number,
    data: { backfill_job_id?: number; notes?: string; status?: string },
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.patch(`/api/admin/noshow_alerts/${id}`, data);
    return response.data;
  },

  getMessages: async (
    alertId: number,
  ): Promise<{ success: boolean; data: NoshowSmsMessage[] }> => {
    const response = await api.get(
      `/api/admin/noshow_alerts/${alertId}/messages`,
    );
    return response.data;
  },
};

const POLL_INTERVAL_MS = 60_000;

export const useNoshowSidebarStats = () => {
  const [stats, setStats] = useState<NoshowAlertStats | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await noshowAlertsAPI.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch {
      // silently fail for sidebar polling
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStats]);

  return {
    needsActionCount:
      (stats?.escalated || 0) +
      (stats?.backfill_requested || 0) +
      (stats?.multi_worker_flagged || 0),
    backfillCount: stats?.backfill_requested || 0,
  };
};
